import { useCallback, useRef } from 'react'
import type { GridApi, GridReadyEvent } from 'ag-grid-community'
import { apiClient } from '../../../shared/api/client'

type User = {
  id?: string
  role?: string
  gridSettings?: Record<string, unknown> | null | undefined
} | null

// Для отдела продаж порядок позиций в заявке всегда из API (priority asc,
// createdAt asc). Любые сохранённые в localStorage `sort: 'asc'/'desc'` из
// старых кликов по заголовку — игнорируем, иначе после ввода цены позиция
// «падает вниз» при активной сортировке.
const SALES_ROLES = new Set(['ADMIN', 'GENERAL_DIRECTOR', 'MANAGER', 'ROP', 'ASSISTANT'])

function stripSortIfSales(state: unknown, role: string | undefined): unknown {
  if (!role || !SALES_ROLES.has(role)) return state
  if (!Array.isArray(state)) return state
  return (state as Record<string, unknown>[]).map((col) => ({
    ...col,
    sort: null,
    sortIndex: null,
  }))
}

/** Есть ли колонка в сохранённом виде таблицы (массив состояний AG Grid). */
function hasSavedCol(state: unknown, colId: string): boolean {
  return (
    Array.isArray(state) &&
    (state as { colId?: unknown }[]).some((col) => col && col.colId === colId)
  )
}

/**
 * Ставит колонку сразу за первой найденной «якорной» колонкой. Индекс считаем
 * по порядку без самой колонки — moveColumns вставляет уже после её изъятия.
 * Нет ни колонки, ни якоря — ничего не делаем.
 */
export function placeColumnAfter(api: GridApi, colId: string, anchors: string[]): void {
  const order = (api.getColumns() ?? []).map((c) => c.getColId())
  if (!order.includes(colId)) return
  const rest = order.filter((id) => id !== colId)
  for (const anchor of anchors) {
    const idx = rest.indexOf(anchor)
    if (idx >= 0) {
      api.moveColumns([colId], idx + 1)
      return
    }
  }
}

export function useColStatePersistence(user: User) {
  const COL_STATE_KEY = `crm_req_items_cols_${user?.id ?? ''}`
  const saveTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  const saveColState = useCallback(
    (api: GridApi) => {
      try {
        const stateJson = JSON.stringify(api.getColumnState())
        localStorage.setItem(COL_STATE_KEY, stateJson)
        // Debounce серверного сохранения (1с)
        if (saveTimerRef.current) clearTimeout(saveTimerRef.current)
        saveTimerRef.current = setTimeout(() => {
          const gs = (user?.gridSettings ?? {}) as Record<string, unknown>
          void apiClient
            .patch('/auth/me', {
              gridSettings: { ...gs, reqItemsCols: JSON.parse(stateJson) },
            })
            .catch(() => {})
        }, 1000)
      } catch {
        /* ignore */
      }
    },
    [COL_STATE_KEY, user?.gridSettings],
  )

  // Восстанавливаем порядок столбцов из сохранённого состояния
  const restoreColState = useCallback(
    (api: GridApi) => {
      // Приоритет: localStorage (быстрый), потом серверные настройки (кросс-устройство)
      let saved = localStorage.getItem(COL_STATE_KEY)
      if (!saved && user?.gridSettings) {
        const gs = user.gridSettings as Record<string, unknown>
        if (gs.reqItemsCols) {
          saved = JSON.stringify(gs.reqItemsCols)
          localStorage.setItem(COL_STATE_KEY, saved)
        }
      }
      // «Даташит» (с 25.09.2026) в старом сохранённом виде отсутствует — AG Grid
      // ставит такие колонки в конец таблицы, и менеджер его не находит.
      let datasheetMissing = false
      if (saved) {
        try {
          const raw: unknown = JSON.parse(saved)
          datasheetMissing = !hasSavedCol(raw, 'datasheetUrl')
          const parsed = stripSortIfSales(raw, user?.role)
          const current = JSON.stringify(api.getColumnState())
          const target = JSON.stringify(parsed)
          if (current !== target) {
            api.applyColumnState({
              state: parsed as Parameters<typeof api.applyColumnState>[0]['state'],
              applyOrder: true,
            })
          }
        } catch {
          /* ignore */
        }
      }
      // Дополнительно: для sales принудительно сбрасываем активную
      // сортировку — даже если applyColumnState проигнорирует sort=null
      // на нескольких колонках, applySort'ом гарантированно очищаем.
      if (user?.role && SALES_ROLES.has(user.role)) {
        try {
          api.applyColumnState({ defaultState: { sort: null } })
        } catch {
          /* ignore */
        }
      }
      // Гарантия: чекбокс всегда первый слева, а стрелочка «открыть позицию»
      // всегда видна прямо перед partNumber. Старое сохранённое состояние
      // колонок могло добавлять новый select-столбец в конец таблицы.
      try {
        api.applyColumnState({
          state: [
            { colId: 'select', hide: false, pinned: 'left' },
            { colId: 'open', hide: false, pinned: null },
          ],
        })
      } catch {
        /* ignore */
      }
      // Принудительно ставим чекбокс первым, а стрелочку прямо перед
      // колонкой «Арт.».
      try {
        const cols = api.getColumns() ?? []
        if (cols.some((c) => c.getColId() === 'select')) api.moveColumns(['select'], 0)
        const partIdx = cols.findIndex((c) => c.getColId() === 'partNumber')
        if (partIdx >= 0) api.moveColumns(['open'], partIdx)
      } catch {
        /* ignore */
      }
      // Даташит — рядом с артикулом, как в виде по умолчанию: за «Кол-во», а
      // если её нет — за «Арт.». Колонки нетарифки (customs*) остаются в конце —
      // так задумано. Сохранённый вид с даташитом не трогаем: место выбрал сам
      // пользователь.
      if (datasheetMissing) {
        try {
          placeColumnAfter(api, 'datasheetUrl', ['qty', 'partNumber'])
        } catch {
          /* ignore */
        }
      }
    },
    [COL_STATE_KEY, user?.gridSettings, user?.role],
  )

  const onGridReady = useCallback(
    (params: GridReadyEvent) => {
      restoreColState(params.api)
    },
    [restoreColState],
  )

  const resetColState = useCallback(() => {
    try {
      localStorage.removeItem(COL_STATE_KEY)
      const gs = user?.gridSettings ?? {}
      const cleaned = { ...(gs as Record<string, unknown>) }
      delete cleaned.reqItemsCols
      void apiClient.patch('/auth/me', { gridSettings: cleaned }).catch(() => {})
    } catch {
      /* ignore */
    }
  }, [COL_STATE_KEY, user?.gridSettings])

  return { COL_STATE_KEY, saveColState, onGridReady, resetColState }
}
